import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import AppIconButton from './AppIconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../../context/ThemeContext';

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterConfig {
  /** Unique key for this filter */
  key: string;
  /** Label shown on the dropdown */
  label: string;
  /** Available options */
  options: FilterOption[];
  /** Current selected value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Min width of the dropdown */
  minWidth?: number;
}

export interface SearchFilterBarProps {
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Current search value */
  searchValue?: string;
  /** Search change handler */
  onSearchChange?: (value: string) => void;
  /** Filter configurations */
  filters?: FilterConfig[];
  /** Action buttons rendered to the right */
  actions?: React.ReactNode;
  /** Additional sx for outer container */
  sx?: Record<string, unknown>;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  filters = [],
  actions,
  sx,
}) => {
  const { theme } = useTheme();
  const input = theme.components.input;

  return (
    <Box
      data-testid="search-filter-bar"
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      {/* Search field */}
      {onSearchChange && (
        <TextField
          data-testid="search-filter-bar-search"
          size="small"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            minWidth: 200,
            flex: { xs: '1 1 100%', sm: '0 1 280px' },
            '& .MuiOutlinedInput-root': {
              backgroundColor: input.background,
              borderRadius: '8px',
              '& fieldset': { borderColor: input.border },
              '&:hover fieldset': { borderColor: input.focus },
              '&.Mui-focused fieldset': { borderColor: input.focus },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlassIcon
                  style={{ width: 18, height: 18, color: theme.gray[400] }}
                />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position="end">
                <AppIconButton
                  data-testid="search-filter-bar-clear"
                  icon={<XMarkIcon style={{ width: 16, height: 16 }} />}
                  tooltip="Clear search"
                  variant="ghost"
                  size="small"
                  onClick={() => onSearchChange('')}
                  sx={{ color: theme.gray[400] }}
                />
              </InputAdornment>
            ) : null,
          }}
        />
      )}

      {/* Filter dropdowns */}
      {filters.map((filter) => (
        <FormControl
          key={filter.key}
          size="small"
          data-testid={`search-filter-bar-filter-${filter.key}`}
          sx={{
            minWidth: filter.minWidth ?? 150,
            flex: { xs: '1 1 45%', sm: '0 0 auto' },
            '& .MuiOutlinedInput-root': {
              backgroundColor: input.background,
              borderRadius: '8px',
              '& fieldset': { borderColor: input.border },
              '&:hover fieldset': { borderColor: input.focus },
              '&.Mui-focused fieldset': { borderColor: input.focus },
            },
          }}
        >
          <InputLabel>{filter.label}</InputLabel>
          <Select
            value={filter.value}
            label={filter.label}
            onChange={(e) => filter.onChange(e.target.value as string)}
          >
            {filter.options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}

      {/* Actions spacer + slot */}
      {actions && (
        <Box
          data-testid="search-filter-bar-actions"
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            ml: { sm: 'auto' },
            flex: { xs: '1 1 100%', sm: '0 0 auto' },
            flexWrap: { xs: 'nowrap', sm: 'wrap' },
            overflowX: { xs: 'auto', sm: 'visible' },
            WebkitOverflowScrolling: 'touch',
            pb: { xs: 0.5, sm: 0 },
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default SearchFilterBar;
